import { Head, Link } from '@inertiajs/react'
import { useState } from 'react'
import { Copy, ExternalLink } from 'lucide-react'

import { AppShell } from '@/layouts/AppShell'
import { PageHeader } from '@/components/layout/PageHeader'
import { Card } from '@/components/ui/Card'
import { Button } from '@/components/ui/Button'
import type { Breadcrumb } from '@/types/shared'

type TeamDetail = {
  id: string
  name: string
  prefecture?: string | null
  createdAt: string
  updatedAt: string
  playersCount: number
  chatroomsCount: number
}

type RegistrationLinks = {
  coach: string
  player: string
}

type AdminTeamShowProps = {
  team: TeamDetail
  registrationLinks: RegistrationLinks
  breadcrumbs: Breadcrumb[]
}

export default function AdminTeamShow({ team, registrationLinks, breadcrumbs }: AdminTeamShowProps) {
  const [copied, setCopied] = useState<string | null>(null)

  const copyLink = async (value: string, type: 'coach' | 'player') => {
    try {
      await navigator.clipboard.writeText(value)
      setCopied(type)
      setTimeout(() => setCopied(null), 2000)
    } catch (error) {
      console.error('リンクのコピーに失敗しました', error)
    }
  }

  return (
    <AppShell>
      <Head title={`チーム詳細｜${team.name}`} />
      <PageHeader
        title={team.name}
        description="チーム情報を確認し、スタッフ・選手向けの登録リンクを共有できます。"
        breadcrumbs={breadcrumbs}
        actions={
          <Button asChild variant="secondary" size="sm">
            <Link href={`/admin/teams/${team.id}/edit`}>編集</Link>
          </Button>
        }
      />

      <div className="grid grid-cols-1 gap-6 xl:grid-cols-[minmax(0,2fr)_minmax(0,1fr)]">
        <Card className="space-y-8" padding="lg">
          <Section title="基本情報">
            <InfoGrid
              items={[
                { label: 'チーム名', value: team.name },
                { label: '都道府県', value: team.prefecture ?? '未設定' },
                { label: '登録日時', value: formatDateTime(team.createdAt) },
                { label: '更新日時', value: formatDateTime(team.updatedAt) },
              ]}
            />
          </Section>

          <Section title="関連データ">
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <KpiCard label="登録選手数" value={team.playersCount} />
              <KpiCard label="関連チャットルーム数" value={team.chatroomsCount} />
            </div>
          </Section>

          <Section title="登録リンク">
            <LinkRow
              label="チームコーチ登録リンク"
              value={registrationLinks.coach}
              copied={copied === 'coach'}
              onCopy={() => copyLink(registrationLinks.coach, 'coach')}
            />
            <LinkRow
              label="選手登録リンク"
              value={registrationLinks.player}
              copied={copied === 'player'}
              onCopy={() => copyLink(registrationLinks.player, 'player')}
            />
          </Section>
        </Card>

        <Card padding="lg" className="space-y-4">
          <h2 className="text-lg font-semibold text-white">ショートカット</h2>
          <div className="space-y-3 text-sm">
            <ShortcutLink href={`/team?team_id=${team.id}`} label="チームダッシュボードを開く" />
            <ShortcutLink href={`/admin/players?team_id=${team.id}`} label="所属選手を確認する" />
          </div>
        </Card>
      </div>
    </AppShell>
  )
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="space-y-4">
      <h2 className="text-lg font-semibold text-white">{title}</h2>
      {children}
    </section>
  )
}

function InfoGrid({ items }: { items: Array<{ label: string; value: React.ReactNode }> }) {
  return (
    <dl className="grid grid-cols-1 gap-4 md:grid-cols-2">
      {items.map((item) => (
        <div key={item.label} className="rounded-2xl border border-white/10 bg-white/5 p-4">
          <dt className="text-xs uppercase tracking-wide text-white/40">{item.label}</dt>
          <dd className="mt-2 text-sm text-white/80">{item.value}</dd>
        </div>
      ))}
    </dl>
  )
}

function KpiCard({ label, value }: { label: string; value: number }) {
  return (
    <div className="rounded-2xl border border-white/10 bg-white/5 p-5">
      <p className="text-xs uppercase tracking-wider text-white/40">{label}</p>
      <p className="mt-2 text-3xl font-semibold text-white">{value.toLocaleString()}</p>
    </div>
  )
}

function LinkRow({
  label,
  value,
  onCopy,
  copied,
}: {
  label: string
  value: string
  onCopy: () => void
  copied: boolean
}) {
  return (
    <div className="space-y-2">
      <p className="text-xs uppercase tracking-wide text-white/40">{label}</p>
      <div className="flex items-center gap-3">
        <input
          value={value}
          readOnly
          className="flex-1 rounded-xl border border-white/10 bg-white/5 px-3 py-2 text-sm text-white/80"
        />
        <Button type="button" variant="secondary" size="sm" onClick={onCopy}>
          <Copy className="mr-2 h-4 w-4" />
          {copied ? 'コピー済み' : 'コピー'}
        </Button>
      </div>
    </div>
  )
}

function ShortcutLink({ href, label }: { href: string; label: string }) {
  return (
    <Link
      href={href}
      className="group flex items-center justify-between rounded-2xl border border-white/10 bg-white/5 px-4 py-3 text-white/80 transition hover:border-white/20 hover:bg-white/10"
    >
      <span>{label}</span>
      <ExternalLink className="h-4 w-4 text-white/40 group-hover:text-white/60" />
    </Link>
  )
}

function formatDateTime(value: string) {
  return new Date(value).toLocaleString('ja-JP', {
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}
